import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import Breadcrumb from 'src/components/shared/Breadcrumb'
import AuthorBiography from './AuthorBiography'
import Pagination from '../../shared/Pagination'
import PageCategoriesPostsLayout from '../../shared/PageCategoriesPostsLayout'
import { beautifyUrlPath } from '../../../common/utils'
import CustomJsonLd from 'src/components/shared/CustomJsonLd'
import { APP_BASE_PATH } from 'src/common/constants'

const AuthorLayout = ({
  author,
  authorPosts: { items, total, currentPage },
}) => {
  const breadcrumbs = [
    {
      label: 'Blog',
      path: '/blog',
    },
    {
      label: author.fullName,
      path: '',
    },
  ]

  const jsonLdData = useMemo(() => {
    const autorData = {
      '@context': 'https://schema.org/',
      '@type': ['ProfilePage'],
      '@id': `${APP_BASE_PATH()}/autor/${author.slug}`,
      mainEntity: {
        '@type': 'Person',
        name: author.fullName,
        jobTitle: 'Senior Writer',
        description: author.metaDescription,
        url: `${APP_BASE_PATH()}/autor/${author.slug}`,
        image: author?.avatar?.url,
        worksFor: [
          {
            '@type': 'Organization',
            name: 'Freedom Debt Relief',
          },
        ],
      },
      headline: author.metaTitle,
      description: author.metaDescription,
      publisher: {
        '@type': 'Organization',
        name: 'Freedom Debt Relief',
        legalName: 'Freedom Debt Relief LLC',
        description:
          "Freedom Debt Relief is America's leader in credit card debt relief and debt consolidation to help clients lower their monthly payments without bankruptcy or loans.",
        url: 'https://www.freedomdebtrelief.com/#organization',
        sameAs: [
          'https://twitter.com/freedomdebt',
          'https://www.facebook.com/freedomdebtrelief/',
          'https://www.linkedin.com/company/freedom-debt-relief/',
          'https://www.youtube.com/freedomdebtrelief',
          'https://www.instagram.com/freedomdebtrelief/',
          'https://www.google.com/search?kgmid=/g/11stymh6d3',
          'https://www.crunchbase.com/organization/freedom-debt-relief-3bd9 ',
        ],
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.freedomdebtrelief.com/next-assets/fdr-logo.webp',
          width: '160',
          height: '50',
        },
      },
      ...(author.aluminiOf
        ? {
            alumniOf: {
              '@type': 'EducationalOrganization',
              name: author.aluminiOf.name,
              url: author.aluminiOf.url,
            },
          }
        : {}),
      ...(author.hasCredential
        ? {
            hasCredential: {
              '@type': 'EducationalOccupationalCredential',
              name: author.hasCredential?.hasCredentialName,
              issuer: {
                '@type': 'Organization',
                name: author.hasCredential?.hasCredentialIssuerName,
                url: author.hasCredential?.hasCredentialIssuerUrl,
              },
            },
          }
        : {}),
      hasCertification: [
        ...(author?.hasCertificationsCollection?.items?.length
          ? author?.hasCertificationsCollection?.items.map((c) => ({
              '@type': 'EducationalOccupationalCredential',
              name: c.hasCertificationName,
              issuer: {
                '@type': 'Person',
                name: c.hasCertificationIssuerName,
                url: c.hasCertificationIssuerUrl,
              },
            }))
          : []),
      ],
    }

    return autorData
  }, [author])

  const authorData = useMemo(() => {
    if (items?.length) {
      return items.map((item, i) => ({
        ...item,
        thumbnailAsset: item.imageAsset,
        publishedAt: item.updatedAt,
        category: {
          type: 'newsroom',
          slug: 'author',
        },
        path: beautifyUrlPath(item.slug),
        key: item.slug,
      }))
    }
  }, [items])

  return (
    <>
      <CustomJsonLd id={'articleLayout'} data={jsonLdData} />
      <div
        className="mx-auto mb-16 max-w-screen-lg lg:px-3 xl:px-0"
        data-testid="author-layout"
      >
        <div className="px-5 py-4 md:py-5 lg:px-0">
          <Breadcrumb items={breadcrumbs} />
        </div>
        <div
          className="border-t border-gray-190"
          data-testid="biography-container"
        >
          <AuthorBiography author={author} />
        </div>
        <div
          className="m-5 block border-t border-gray-190 pt-6 lg:mx-0 lg:flex lg:space-x-2 lg:px-0"
          data-testid="posts-section"
        >
          <div className="w-full" data-testid="author-posts">
            <PageCategoriesPostsLayout data={authorData} />
            <Pagination
              itemCount={total}
              currentPageNumber={Number(currentPage)}
              link={author?.slug || ''}
            />
          </div>
        </div>
      </div>
    </>
  )
}

AuthorLayout.propTypes = {
  author: PropTypes.object.isRequired,
  authorPosts: PropTypes.object.isRequired,
}
export default AuthorLayout
